import React from "react";
import { useMusicPlayer } from "../MusicPlayerContext";
import { Button } from "./ui/button";
import { Play, Pause, SkipBack, SkipForward, Minimize2, Music, Square } from "lucide-react";

export const MiniPlayer = () => {
  const player = useMusicPlayer();
  if (!player) return null;
  const { visible, minimized, currentTrack, isPlaying, toggle, next, prev, closePlayer, setMinimized } = player;
  if (!visible || !currentTrack) return null;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed right-4 z-[9999] w-12 h-12 rounded-full bg-slate-900 text-white shadow-2xl hover:scale-105 transition flex items-center justify-center bottom-20 md:bottom-4"
        data-testid="mini-player-expand-btn"
        aria-label="Expand music player"
      >
        <Music className={`w-5 h-5 ${isPlaying ? "animate-pulse" : ""}`} />
      </button>
    );
  }

  return (
    <div
      className="fixed left-0 right-0 z-[9999] bg-slate-900 text-white border-t border-slate-700 shadow-2xl bottom-14 md:bottom-0"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="mini-player-bar"
    >
      <div className="max-w-6xl mx-auto px-2 sm:px-3 py-2 flex items-center gap-2 sm:gap-3">
        {currentTrack.thumbnail_url && (
          <img
            src={currentTrack.thumbnail_url}
            alt=""
            className="w-10 h-10 sm:w-12 sm:h-12 rounded object-cover shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs sm:text-sm font-semibold truncate" data-testid="mini-player-title">{currentTrack.title}</div>
          <div className="text-[10px] sm:text-xs text-slate-400 truncate">{currentTrack.artist}</div>
        </div>
        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          <Button
            size="icon"
            variant="ghost"
            className="text-white hover:bg-slate-800 h-8 w-8 sm:h-9 sm:w-9 hidden sm:inline-flex"
            onClick={prev}
            data-testid="mini-player-prev-btn"
            aria-label="Previous"
          >
            <SkipBack className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            className="bg-white text-slate-900 hover:bg-slate-200 h-9 w-9 sm:h-10 sm:w-10 rounded-full shrink-0"
            onClick={toggle}
            data-testid="mini-player-toggle-btn"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white h-9 sm:h-10 px-2 sm:px-3 rounded-full ml-0.5 sm:ml-1"
            onClick={closePlayer}
            data-testid="mini-player-stop-btn"
            aria-label="Stop music"
            title="Stop music"
          >
            <Square className="w-3.5 h-3.5 sm:mr-1 fill-current" />
            <span className="text-xs font-semibold hidden sm:inline">Stop</span>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-white hover:bg-slate-800 h-8 w-8 sm:h-9 sm:w-9 hidden sm:inline-flex"
            onClick={next}
            data-testid="mini-player-next-btn"
            aria-label="Next"
          >
            <SkipForward className="w-4 h-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-white hover:bg-slate-800 h-8 w-8 sm:h-9 sm:w-9 ml-0.5 sm:ml-1"
            onClick={() => setMinimized(true)}
            data-testid="mini-player-minimize-btn"
            aria-label="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
